import {
  CORE_MODULES,
  MOBILE_BUILDER_MODULES,
  MOBILE_SIGNATURE_MODULES,
  getModuleMeta,
  isHiddenMainFlowModule,
} from './moduleCatalog';
import { GIFT_FORMATS, getGiftFormatForOpeningMode } from './giftFormats';

export const TEMPLATE_MODULE_STRATEGY = {
  mom: {
    defaultModules: ['primary_media', 'text', 'surprise_box', 'voice_postcard', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['frame_gallery', 'tiny_museum'],
    optionalModules: ['memory_map', 'song_dedication', 'polaroid_booth', 'then_now'],
  },
  partner: {
    defaultModules: ['primary_media', 'text', 'then_now', 'surprise_box', 'memory_map', 'voice_postcard', 'keepsake_poster'],
    creativeMoments: ['song_dedication', 'polaroid_booth', 'tiny_museum'],
    optionalModules: ['guestbook', 'frame_gallery'],
  },
  wedding: {
    defaultModules: ['primary_media', 'text', 'memory_map', 'surprise_box', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['polaroid_booth', 'song_dedication', 'tiny_museum'],
    optionalModules: ['voice_postcard', 'frame_gallery', 'then_now'],
  },
  birthday: {
    defaultModules: ['primary_media', 'text', 'surprise_box', 'voice_postcard', 'polaroid_booth', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['frame_gallery', 'song_dedication', 'then_now'],
    optionalModules: ['memory_map', 'tiny_museum'],
  },
  bestie: {
    defaultModules: ['primary_media', 'memory_map', 'text', 'surprise_box', 'polaroid_booth', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['then_now', 'song_dedication', 'frame_gallery'],
    optionalModules: ['tiny_museum', 'voice_postcard'],
  },
  graduation: {
    defaultModules: ['primary_media', 'memory_map', 'tiny_museum', 'text', 'voice_postcard', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['polaroid_booth', 'then_now', 'song_dedication'],
    optionalModules: ['frame_gallery'],
  },
  remembrance: {
    defaultModules: ['primary_media', 'voice_postcard', 'timeline', 'guestbook', 'keepsake_poster'],
    creativeMoments: ['tiny_museum', 'frame_gallery', 'memory_map'],
    optionalModules: ['text', 'surprise_box'],
    moduleLabels: {
      primary_media: {
        label: 'Memory Archive',
        shortLabel: 'Archive',
        role: 'Collect photos and short clips that hold the clearest memories.',
      },
      voice_postcard: {
        label: 'Voice Archive',
        shortLabel: 'Voice',
        role: 'Save spoken stories, blessings, and words the family may want to hear again.',
      },
      timeline: {
        label: 'Timeline',
        shortLabel: 'Time',
        role: 'Arrange important moments into a gentle life and memory timeline.',
      },
      guestbook: {
        label: 'Family Messages',
        shortLabel: 'Messages',
        role: 'Invite family and close loved ones to leave memories or notes.',
      },
    },
  },
  pet_memorial: {
    defaultModules: ['primary_media', 'text', 'tiny_museum', 'voice_postcard', 'keepsake_poster'],
    creativeMoments: ['frame_gallery', 'memory_map'],
    optionalModules: ['guestbook', 'surprise_box'],
  },
  new_baby: {
    defaultModules: ['memory_map', 'guestbook', 'primary_media', 'timeline', 'voice_postcard', 'keepsake_poster'],
    creativeMoments: ['frame_gallery', 'tiny_museum', 'then_now'],
    optionalModules: ['surprise_box', 'polaroid_booth', 'text'],
    moduleLabels: {
      memory_map: {
        label: 'Milestones',
        shortLabel: 'Firsts',
        role: 'Save tiny firsts, dates, and family moments as a soft milestone path.',
      },
      guestbook: {
        label: 'Family Wishes',
        shortLabel: 'Wishes',
        role: 'Collect short blessings and welcome notes from family and loved ones.',
      },
      primary_media: {
        label: 'Baby Gallery',
        shortLabel: 'Gallery',
        role: 'Add the first baby photos and family moments everyone will want to revisit.',
      },
      timeline: {
        label: 'Growth Timeline',
        shortLabel: 'Growth',
        role: 'Keep adding growth moments so the NFC gift becomes a living baby album.',
      },
    },
  },
  thank_you: {
    defaultModules: ['text', 'surprise_box', 'keepsake_poster'],
    creativeMoments: ['primary_media', 'frame_gallery', 'tiny_museum'],
    optionalModules: ['voice_postcard', 'guestbook', 'memory_map', 'song_dedication'],
  },
};

export const FORMAT_MODULE_STRATEGY = Object.fromEntries(
  Object.entries(GIFT_FORMATS).map(([key, format]) => [
    key,
    {
      defaultModules: format.recommendedModules || [],
      creativeMoments: MOBILE_SIGNATURE_MODULES
        .filter((moduleType) => !(format.recommendedModules || []).includes(moduleType)),
      optionalModules: CORE_MODULES
        .map((module) => module.id)
        .filter((moduleType) => !(format.recommendedModules || []).includes(moduleType)),
    },
  ])
);

export const getTemplateModuleStrategy = (pageData = {}) => {
  const templateKey = pageData.templateKey || pageData.presetKey || pageData.preset || pageData.template;
  const selectedFormat = pageData.giftFormat || getGiftFormatForOpeningMode(pageData.openingMode);
  return TEMPLATE_MODULE_STRATEGY[templateKey] || FORMAT_MODULE_STRATEGY[selectedFormat] || FORMAT_MODULE_STRATEGY.gift_reveal;
};

export const getMobileBuilderModuleGroups = (pageData = {}) => {
  const strategy = getTemplateModuleStrategy(pageData);
  const moduleLabels = strategy.moduleLabels || {};
  const seen = new Set();

  const toModuleList = (moduleTypes = []) => moduleTypes
    .filter(Boolean)
    .filter((moduleType) => !isHiddenMainFlowModule(moduleType))
    .filter((moduleType) => {
      if (seen.has(moduleType)) return false;
      seen.add(moduleType);
      return true;
    })
    .map((moduleType) => ({
      ...getModuleMeta(moduleType),
      ...(moduleLabels[moduleType] || {}),
    }));

  return {
    recommended: toModuleList(strategy.defaultModules || []),
    signature: toModuleList(strategy.creativeMoments || []),
    more: toModuleList([...(strategy.optionalModules || []), ...MOBILE_BUILDER_MODULES]),
  };
};
